import { useState } from 'react'
import { Link } from 'react-router'
import { useLinkNote, useNotes, useTaskNotes, useUnlinkNote } from '../../hooks/useTaskNotes.ts'
import { relativeTime } from '../../lib/relativeTime.ts'
import { Picker } from '../../ui/Picker.tsx'
import { ErrorState } from '../../ui/primitives.tsx'
import styles from './drawer.module.css'

/**
 * The notes attached to a task (ADR 0012).
 *
 * Attaching never creates or copies anything: a note stays one document, and this section is a list
 * of pointers to it. That is the whole reason the section says "podepnij" and not "dodaj notatkę" —
 * the second wording would promise a note that belongs to this task, which is exactly what the
 * decision refuses to build.
 */
export function TaskNotes({ taskId }: { taskId: number }): React.JSX.Element {
  const linked = useTaskNotes(taskId)
  const unlink = useUnlinkNote()
  const [picking, setPicking] = useState(false)

  return (
    <div className={styles.notesSection}>
      <div className={styles.sectionRow}>
        <p className={styles.sectionLabel}>Notatki</p>
        {!picking && (
          <button type="button" className={styles.sectionAction} onClick={() => setPicking(true)}>
            podepnij
          </button>
        )}
      </div>

      {linked.isError ? (
        <ErrorState
          text="Nie udało się wczytać podpiętych notatek."
          onRetry={() => void linked.refetch()}
        />
      ) : (
        <div className={styles.linkedNotes}>
          {(linked.data ?? []).map((note) => (
            <div key={note.id} className={styles.linkedNote}>
              <Link className={styles.linkedNoteLink} to={`/notes?note=${note.id}`}>
                {note.title}
              </Link>
              <span className={styles.linkedNoteTime}>{relativeTime(note.updatedAt)}</span>
              <button
                type="button"
                className={styles.linkedNoteRemove}
                title="Odepnij notatkę od tego zadania"
                onClick={() => unlink.mutate({ taskId, noteId: note.id })}
              >
                odepnij
              </button>
            </div>
          ))}

          {!linked.isPending && (linked.data ?? []).length === 0 && !picking && (
            <p className={styles.linkedEmpty}>Nie ma podpiętych notatek.</p>
          )}
        </div>
      )}

      {picking && (
        <NotePicker
          taskId={taskId}
          alreadyLinked={(linked.data ?? []).map((n) => n.id)}
          onClose={() => setPicking(false)}
        />
      )}
    </div>
  )
}

/**
 * Picks an existing note by title.
 *
 * No "create a new note here" button, deliberately. A note written from inside a task drawer would
 * be a note nobody ever opens again; notes are written in the notes module, and this only points
 * at them.
 */
function NotePicker({
  taskId,
  alreadyLinked,
  onClose,
}: {
  taskId: number
  alreadyLinked: number[]
  onClose: () => void
}): React.JSX.Element {
  const notes = useNotes()
  const link = useLinkNote()

  const candidates = (notes.data ?? []).filter((n) => !alreadyLinked.includes(n.id))

  return (
    <Picker
      label="Szukaj notatki do podpięcia"
      placeholder="Szukaj notatki…"
      options={candidates}
      match={(note, needle) => note.title.toLowerCase().includes(needle)}
      renderOption={(note) => <span className={styles.pickerNoteTitle}>{note.title}</span>}
      emptyText={(hasQuery) => {
        if (notes.isPending) return 'Wczytuję notatki…'
        if (hasQuery) return 'Żadna notatka nie pasuje.'
        return (notes.data ?? []).length === 0
          ? 'Nie ma jeszcze żadnej notatki — załóż ją w zakładce Notatki.'
          : 'Wszystkie notatki są już podpięte.'
      }}
      onPick={(note) => {
        link.mutate({ taskId, noteId: note.id })
        onClose()
      }}
      onClose={onClose}
    />
  )
}
